import { NgStyle } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToolConfigWebhook } from '@shared/index';
import { removeFromArray } from '@reflact/tsutil';
import { SkillIdInputComponent } from 'src/app/shared/components/ai-bot/utils/skill-id-input/skill-id-input.component';
import { SkillUsageListComponent } from 'src/app/shared/components/ai-bot/utils/skill-usage-list/skill-usage-list.component';
import { BotService } from 'src/app/shared/services/bot.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { SkillService } from 'src/app/shared/services/skill.service';
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { SecretPlaceholderListComponent } from "../../../../../../shared/components/secret-placeholder-list/secret-placeholder-list.component";
import { DrawerModule } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TableModule } from "primeng/table";
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { WebhooksTabDetailsComponent } from './webhooks-tab-details/webhooks-tab-details.component';

@Component({
  selector: 'app-webhooks-tab',
  imports: [NgStyle, 
    FormsModule,
    SkillIdInputComponent,
    SkillUsageListComponent,
    ManualLinkComponent,
    SecretPlaceholderListComponent,
    MenuModule,
    ButtonModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    TableModule,
    BulkActionBarComponent,
    InputTextModule,
    ButtonModule,
    MessageModule,
    TooltipModule,
    DrawerModule,
    WebhooksTabDetailsComponent
  ],
  templateUrl: './webhooks-tab.component.html',
  styleUrl: './webhooks-tab.component.scss',
  providers: [ConfirmationService]
})
export class WebhooksTabComponent implements OnInit {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => this.removeSelectedCustomWebhooks()
      }
    ];
  }

  protected customWebhookName: string = '';
  protected customWebhookUrl: string = '';

  public requiredValidator: ValidatorFn[] = [Validators.required];
  public selectedCustomWebhooks: ToolConfigWebhook[] = [];

  public get allSelected(): boolean {
    return this.customWebhooks.length > 0 && this.selectedCustomWebhooks.length === this.customWebhooks.length;
  }

  constructor(
    public router: Router,
    public rss: RouteShareService,
    public skillService: SkillService,
    private botService: BotService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) { }

  public ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['open']) {
        this.showDetailDrawer(params['open'] as string);
      }
    });
  }

  public get customWebhooks(): ToolConfigWebhook[] {
    return this.rss.botConfig.toolConfigs.filter((tc) => tc.tool === 'webhook') as ToolConfigWebhook[];
  }

  protected async addCustomWebhook(): Promise<void> {
    const newCustomWebhook: ToolConfigWebhook = {
      skillId: '',
      tool: 'webhook',
      name: this.customWebhookName.trim(),
      description: '',
      enabled: true,
      params: [],
      id: '',
      url: this.customWebhookUrl.trim(),
      authMethod: 'none',
      authData: { password: '', user: '' },
      urlMethod: 'get'
    };
    newCustomWebhook.skillId = await this.botService.suggestSkill(this.rss.botConfig._id, newCustomWebhook.description, newCustomWebhook.name);
    const existingIds = new Set(this.customWebhooks.map(t => t.id));
    this.rss.botConfig.toolConfigs.unshift(newCustomWebhook);
    await this.rss.saveBotConfigField('toolConfigs');
    this.customWebhookName = '';
    this.customWebhookUrl = '';
    const savedWebhook = this.customWebhooks.find(t => !existingIds.has(t.id));
    if (savedWebhook) {
      this.showDetailDrawer(savedWebhook.id);
    }
  }

  public copyCustomWebhook(id: string): void {
    const itemToCopy = this.rss.botConfig.toolConfigs.find(tc => tc.id === id);
    if (itemToCopy) {
      const itemWithoutId = structuredClone(itemToCopy);
      itemWithoutId.id = '';
      this.rss.botConfig.toolConfigs.unshift(itemWithoutId);
      void this.rss.saveBotConfigField('toolConfigs');
    }
  }

  protected removeCustomWebhook(id: string): void {
    removeFromArray(this.rss.botConfig.toolConfigs, tc => tc.id === id);
    this.selectedCustomWebhooks = this.selectedCustomWebhooks.filter(webhook => webhook.id !== id);
    void this.rss.saveBotConfigField('toolConfigs');
  }

  public toggleAll(checked: boolean): void {
    this.selectedCustomWebhooks = checked ? [...this.customWebhooks] : [];
  }

  public removeSelectedCustomWebhooks(): void {
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-skills-header:Ausgewählte Einträge löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-skills-message:Möchtest du die ausgewählten Einträge wirklich löschen?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        const selectedIds = new Set(this.selectedCustomWebhooks.map(webhook => webhook.id));
        this.rss.botConfig.toolConfigs = this.rss.botConfig.toolConfigs.filter(tool => !selectedIds.has(tool.id));
        this.selectedCustomWebhooks = [];
        await this.rss.saveBotConfigField('toolConfigs');
      }
    });
  }

  public customWebhookContextMenu: MenuItem[] = [];
  public selectedWebhookId: string = '';
  public detailDrawer: boolean = false;

  public showDetailDrawer(id: string): void {
    this.selectedWebhookId = id;
    this.detailDrawer = true;
  }

  public openCustomWebhookMenu(event: Event, menu: Menu, custom: ToolConfigWebhook) {
    this.customWebhookContextMenu = this.buildCustomWebhookMenuItems(custom);
    menu.toggle(event);
  }

  private buildCustomWebhookMenuItems(custom: ToolConfigWebhook): MenuItem[] {
    const items: MenuItem[] = [];

    // Bearbeiten
    items.push({
      label: $localize`:@@edit:Bearbeiten`,
      icon: 'ri ri-pencil-fill',
      command: () => this.showDetailDrawer(custom.id)
    });

    // Duplizieren
    items.push({
      label: $localize`:@@copy:Duplizieren`,
      icon: 'ri ri-file-copy-fill',
      command: () => { this.copyCustomWebhook(custom.id); }
    });

    // Löschen
    items.push({
      label: $localize`:@@delete:Löschen`,
      icon: 'ri ri-close-large-fill',
      command: () => { this.removeCustomWebhook(custom.id); }
    });

    return items;
  }
}
